/**
 * État d'accès d'un tenant, servi par `GET /auth/me` (#670).
 *
 * Le niveau est **dérivé, jamais stocké** : il change avec le temps qui passe
 * — J+7, J+15, J+30 — sans qu'aucun événement ne survienne. Il ne doit donc pas
 * entrer dans le store persisté : une session rouverte le lendemain afficherait
 * l'état de la veille.
 */
export type TenantAccessLevel = 'full' | 'read_only' | 'suspended';

export type TenantAccessState = {
  level: TenantAccessLevel;
  /** Depuis quand l'impayé court. */
  pastDueSince: string | null;
  /** Date de la prochaine bascule — permet de l'annoncer avant qu'elle ne tombe. */
  nextTransitionAt: string | null;
  /** Motif d'une suspension administrative : elle ne se lève pas comme un impayé. */
  adminSuspensionReason: string | null;
};

export type AccessNoticeTone = 'warning' | 'danger';

export type AccessNotice = {
  tone: AccessNoticeTone;
  title: string;
  body: string;
  /** Jours restants avant la prochaine bascule, quand elle est connue et à venir. */
  daysLeft: number | null;
};

/** Jours pleins jusqu'à `iso`, ou `null` si la date est absente ou déjà passée. */
export function daysUntil(iso: string | null, now: Date = new Date()): number | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((dayStart(new Date(target)) - dayStart(now)) / 86_400_000);
  return days >= 0 ? days : null;
}

/** « dans 3 jours », « demain », « aujourd'hui ». */
function whenLabel(days: number | null): string {
  if (days === null) return 'prochainement';
  if (days === 0) return "aujourd'hui";
  if (days === 1) return 'demain';
  return `dans ${days} jours`;
}

/**
 * Seuil d'annonce : en deçà, une échéance encore lointaine encombrerait l'écran
 * de tous les jours sans rien changer à ce qu'on peut faire.
 */
const ANNOUNCE_WITHIN_DAYS = 7;

/**
 * Message à afficher, ou `null` quand il n'y a rien à dire.
 *
 * Trois situations qui ne se lèvent pas de la même façon : une échéance qui
 * approche, une lecture seule en cours, un accès fermé. La suspension
 * administrative est distinguée de l'impayé — l'une se lève par une décision,
 * l'autre par un paiement.
 */
export function accessNotice(
  access: TenantAccessState | null | undefined,
  now: Date = new Date(),
): AccessNotice | null {
  if (!access) return null;
  const daysLeft = daysUntil(access.nextTransitionAt, now);

  if (access.level === 'suspended') {
    return {
      tone: 'danger',
      title: 'Accès suspendu',
      body: access.adminSuspensionReason
        ? `Motif : ${access.adminSuspensionReason}. Contactez votre gestionnaire Suivora pour rétablir le service.`
        : 'Contactez votre gestionnaire Suivora pour rétablir le service.',
      daysLeft: null,
    };
  }

  if (access.level === 'read_only') {
    return {
      tone: 'danger',
      title: 'Espace en lecture seule',
      body:
        `Un règlement est en attente. La consultation reste ouverte, la saisie reprendra dès la régularisation.` +
        (daysLeft !== null
          ? ` Sans régularisation, l’accès sera suspendu ${whenLabel(daysLeft)}.`
          : ''),
      daysLeft,
    };
  }

  // Niveau `full` : on n'alerte que si la bascule est proche. Annoncer une
  // échéance à trois semaines userait le bandeau sans rien apporter.
  if (daysLeft !== null && daysLeft <= ANNOUNCE_WITHIN_DAYS && access.pastDueSince) {
    return {
      tone: 'warning',
      title: `Passage en lecture seule ${whenLabel(daysLeft)}`,
      body: 'Un prélèvement a échoué. Régularisez pour conserver la saisie — la consultation, elle, restera ouverte.',
      daysLeft,
    };
  }

  return null;
}

/**
 * Message expliquant pourquoi une écriture est refusée, ou `null` quand elle est
 * permise.
 *
 * Mêmes exemptions que le garde backend, et pour les mêmes raisons : hors
 * contexte tenant (`access` nul) rien n'est bloqué — le super-admin éditeur n'est
 * pas concerné, et l'inverse serait absurde, l'impayé bloquerait celui qui peut
 * le résoudre.
 */
export function writeBlockedMessage(access: TenantAccessState | null | undefined): string | null {
  if (!access || access.level === 'full') return null;
  if (access.level === 'suspended') {
    return access.adminSuspensionReason
      ? `Accès suspendu — ${access.adminSuspensionReason}. Contactez votre gestionnaire Suivora.`
      : 'Accès suspendu. Contactez votre gestionnaire Suivora pour rétablir le service.';
  }
  return 'Espace en lecture seule : un règlement est en attente. La consultation reste ouverte ; la saisie reprendra dès la régularisation.';
}
